import { useEffect, useMemo, useState, type ChangeEvent, type FormEvent } from 'react';
import {
  ArrowUpRight,
  BriefcaseBusiness,
  Building2,
  CalendarDays,
  CheckCircle2,
  Clock3,
  ExternalLink,
  FileText,
  Mail,
  Pencil,
  Plus,
  Search,
  Sparkles,
  Target,
  Trash2,
  UserRound,
  Video,
  X,
} from 'lucide-react';
import { useAuth } from '../app/AuthContext';
import { api } from '../lib/api';
import {
  createDemoInterview,
  deleteDemoInterview,
  emptyInterview,
  loadDemoInterviews,
  updateDemoInterview,
  type InterviewInput,
  type InterviewRecord,
  type InterviewStatus,
} from '../lib/interviews';

const statuses: { value: InterviewStatus; label: string }[] = [
  { value: 'applied', label: 'Applied' },
  { value: 'scheduled', label: 'Scheduled' },
  { value: 'interviewing', label: 'Interviewing' },
  { value: 'offer', label: 'Offer' },
  { value: 'rejected', label: 'Closed' },
  { value: 'withdrawn', label: 'Withdrawn' },
];

const statusLabel = (status: InterviewStatus) => statuses.find((item) => item.value === status)?.label ?? status;

function formatDate(value: string, includeTime = true) {
  if (!value) return 'Date not added';
  const date = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  if (Number.isNaN(date.getTime())) return value;
  return new Intl.DateTimeFormat('en-IN', includeTime
    ? { weekday: 'short', day: 'numeric', month: 'short', hour: 'numeric', minute: '2-digit' }
    : { day: 'numeric', month: 'short', year: 'numeric' }).format(date);
}

function relativeDate(value: string) {
  if (!value) return '';
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const target = new Date(value.length === 10 ? `${value}T12:00:00` : value);
  target.setHours(0, 0, 0, 0);
  const days = Math.round((target.getTime() - today.getTime()) / 86_400_000);
  if (days === 0) return 'Today';
  if (days === 1) return 'Tomorrow';
  if (days > 1) return `In ${days} days`;
  return `${Math.abs(days)} days ago`;
}

const safeJobUrl = (value: string) => /^https?:\/\//i.test(value) ? value : '';

export function InterviewsPage() {
  const { isDemo } = useAuth();
  const [records, setRecords] = useState<InterviewRecord[]>([]);
  const [selectedId, setSelectedId] = useState<number | null>(null);
  const [query, setQuery] = useState('');
  const [filter, setFilter] = useState<'all' | InterviewStatus>('all');
  const [form, setForm] = useState<InterviewInput>(emptyInterview());
  const [editingId, setEditingId] = useState<number | null>(null);
  const [formOpen, setFormOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');

  const load = async () => {
    setLoading(true);
    setError('');
    try {
      const next = isDemo ? loadDemoInterviews() : (await api.interviews()).interviews;
      setRecords(next);
      setSelectedId((current) => next.some((record) => record.id === current) ? current : next[0]?.id ?? null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not load interviews.');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { void load(); }, [isDemo]);

  const visible = useMemo(() => records
    .filter((record) => filter === 'all' || record.status === filter)
    .filter((record) => `${record.company} ${record.role} ${record.stage} ${record.notes}`.toLowerCase().includes(query.trim().toLowerCase()))
    .sort((a, b) => {
      if (!a.interviewAt) return 1;
      if (!b.interviewAt) return -1;
      return new Date(a.interviewAt).getTime() - new Date(b.interviewAt).getTime();
    }), [records, filter, query]);
  const selected = records.find((record) => record.id === selectedId) ?? null;
  const now = Date.now();
  const upcoming = records.filter((record) => record.interviewAt && new Date(record.interviewAt).getTime() >= now && ['scheduled', 'interviewing'].includes(record.status)).length;
  const offers = records.filter((record) => record.status === 'offer').length;
  const followUps = records.filter((record) => record.followUpAt && new Date(`${record.followUpAt}T23:59:59`).getTime() >= now && !['rejected', 'withdrawn'].includes(record.status)).length;

  const openCreate = () => {
    setEditingId(null);
    setForm(emptyInterview());
    setError('');
    setFormOpen(true);
  };

  const openEdit = (record: InterviewRecord) => {
    const { id: _id, createdAt: _createdAt, updatedAt: _updatedAt, ...input } = record;
    setEditingId(record.id);
    setForm(input);
    setError('');
    setFormOpen(true);
  };

  const change = (event: ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value } = event.target;
    setForm((current) => ({ ...current, [name]: value }));
  };

  const save = async (event: FormEvent) => {
    event.preventDefault();
    if (!form.company.trim() || !form.role.trim()) {
      setError('Company and role are required.');
      return;
    }
    setSaving(true);
    setError('');
    try {
      if (isDemo) {
        const next = editingId ? updateDemoInterview(editingId, form) : createDemoInterview(form);
        setRecords(next);
        setSelectedId(editingId ?? next[0].id);
      } else if (editingId) {
        const { interview } = await api.updateInterview(editingId, form);
        setRecords((current) => current.map((record) => record.id === editingId ? interview : record));
        setSelectedId(interview.id);
      } else {
        const { interview } = await api.createInterview(form);
        setRecords((current) => [interview, ...current]);
        setSelectedId(interview.id);
      }
      setFormOpen(false);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not save this interview.');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (record: InterviewRecord) => {
    if (!window.confirm(`Delete the ${record.company} interview record? This cannot be undone.`)) return;
    try {
      const next = isDemo ? deleteDemoInterview(record.id) : records.filter((item) => item.id !== record.id);
      if (!isDemo) await api.deleteInterview(record.id);
      setRecords(next);
      setSelectedId(next[0]?.id ?? null);
    } catch (caught) {
      setError(caught instanceof Error ? caught.message : 'Could not delete this interview.');
    }
  };

  return (
    <div className="page interviews-page">
      <header className="page-heading split">
        <div><span className="eyebrow">OPPORTUNITY COMMAND CENTER</span><h1>Interview tracker</h1><p>Keep every company, round, question, and follow-up in one place.</p></div>
        <button className="button primary" type="button" onClick={openCreate}><Plus /> Add interview</button>
      </header>

      <section className="interview-stats">
        <article><span><BriefcaseBusiness /></span><div><strong>{records.length}</strong><small>Tracked processes</small></div></article>
        <article><span><CalendarDays /></span><div><strong>{upcoming}</strong><small>Upcoming interviews</small></div></article>
        <article><span><Sparkles /></span><div><strong>{offers}</strong><small>Offers received</small></div></article>
        <article><span><Clock3 /></span><div><strong>{followUps}</strong><small>Follow-ups planned</small></div></article>
      </section>

      <div className="interview-toolbar">
        <label><Search /><input value={query} onChange={(event) => setQuery(event.target.value)} placeholder="Search company, role, or round…" /></label>
        <div>{(['all', 'scheduled', 'interviewing', 'offer', 'applied'] as const).map((status) => <button type="button" key={status} className={filter === status ? 'active' : ''} onClick={() => setFilter(status)}>{status === 'all' ? 'All' : statusLabel(status)}</button>)}</div>
      </div>

      {error && !formOpen && <div className="interview-error">{error}</div>}
      <div className="interview-content">
        <section className="interview-list">
          {loading ? <div className="interview-empty"><span className="loading-orbit" /><h3>Loading your interviews</h3></div> : visible.length ? visible.map((record) => (
            <button type="button" className={`interview-card ${selectedId === record.id ? 'selected' : ''}`} key={record.id} onClick={() => setSelectedId(record.id)}>
              <span className="company-mark">{record.company.slice(0, 1).toUpperCase()}</span>
              <span className="interview-card-copy">
                <span className="interview-card-top"><b>{record.company}</b><i className={`interview-status status-${record.status}`}>{statusLabel(record.status)}</i></span>
                <strong>{record.role}</strong>
                <span className="interview-meta"><em><Target /> {record.stage || 'Stage not added'}</em><em><CalendarDays /> {formatDate(record.interviewAt)}</em></span>
                {record.nextAction && <span className="next-action"><CheckCircle2 /> {record.nextAction}</span>}
              </span>
              <ArrowUpRight className="card-arrow" />
            </button>
          )) : <div className="interview-empty"><BriefcaseBusiness /><h3>No interviews here yet</h3><p>Save your first opportunity and keep every next step visible.</p><button className="button primary" type="button" onClick={openCreate}><Plus /> Add interview</button></div>}
        </section>

        <aside className="interview-detail">
          {selected ? <>
            <header>
              <div className="detail-company"><span className="company-mark">{selected.company.slice(0, 1).toUpperCase()}</span><div><i className={`interview-status status-${selected.status}`}>{statusLabel(selected.status)}</i><h2>{selected.company}</h2><p>{selected.role}</p></div></div>
              <div className="detail-actions"><button type="button" onClick={() => openEdit(selected)} title="Edit interview"><Pencil /></button><button className="danger" type="button" onClick={() => void remove(selected)} title="Delete interview"><Trash2 /></button></div>
            </header>
            <div className="interview-schedule">
              <div><CalendarDays /><span><small>NEXT INTERVIEW</small><b>{formatDate(selected.interviewAt)}</b><em>{relativeDate(selected.interviewAt)}</em></span></div>
              <div><Video /><span><small>FORMAT</small><b>{selected.mode}</b><em>{selected.location || 'Location not added'}</em></span></div>
            </div>
            <div className="detail-grid">
              <section><span><Target /> CURRENT ROUND</span><p>{selected.stage || 'Not added yet'}</p></section>
              <section><span><UserRound /> RECRUITER / CONTACT</span><p>{selected.contactName || 'Not added yet'}</p>{selected.contactEmail && <a href={`mailto:${selected.contactEmail}`}><Mail /> {selected.contactEmail}</a>}</section>
              <section><span><Sparkles /> PREPARATION FOCUS</span><p>{selected.preparationFocus || 'Add the topics and stories you want to rehearse.'}</p></section>
              <section><span><FileText /> QUESTIONS ASKED</span><p>{selected.questionsAsked || 'Capture the questions after each round while they are fresh.'}</p></section>
              <section className="wide"><span><CheckCircle2 /> NEXT ACTION</span><p>{selected.nextAction || 'No next action added.'}</p>{selected.followUpAt && <small>Follow up {formatDate(selected.followUpAt, false)} · {relativeDate(selected.followUpAt)}</small>}</section>
              <section className="wide"><span><FileText /> NOTES & REFLECTION</span><p>{selected.notes || 'No notes added yet.'}</p></section>
            </div>
            <footer>
              <span>{selected.salaryRange || 'Compensation not added'}</span>
              {safeJobUrl(selected.jobUrl) && <a href={safeJobUrl(selected.jobUrl)} target="_blank" rel="noreferrer">Open job post <ExternalLink /></a>}
            </footer>
          </> : <div className="detail-placeholder"><Target /><h3>Select an interview</h3><p>Its schedule, preparation focus, notes, and follow-up will appear here.</p></div>}
        </aside>
      </div>

      {formOpen && <div className="interview-modal-backdrop" onMouseDown={(event) => { if (event.target === event.currentTarget) setFormOpen(false); }}>
        <form className="interview-form" onSubmit={save}>
          <header><div><span className="eyebrow">{editingId ? 'UPDATE PROCESS' : 'NEW OPPORTUNITY'}</span><h2>{editingId ? 'Edit interview details' : 'Add an interview'}</h2><p>Capture enough context that future-you knows exactly what to do next.</p></div><button type="button" onClick={() => setFormOpen(false)}><X /></button></header>
          <div className="interview-form-body">
            <section><h3><Building2 /> Company and role</h3><div className="form-grid"><label><span>Company *</span><input autoFocus name="company" value={form.company} onChange={change} placeholder="Company name" maxLength={150} /></label><label><span>Role *</span><input name="role" value={form.role} onChange={change} placeholder="Software Engineer" maxLength={150} /></label><label><span>Status</span><select name="status" value={form.status} onChange={change}>{statuses.map((item) => <option key={item.value} value={item.value}>{item.label}</option>)}</select></label><label><span>Current round</span><input name="stage" value={form.stage} onChange={change} placeholder="Technical round 2" maxLength={100} /></label></div></section>
            <section><h3><CalendarDays /> Schedule</h3><div className="form-grid"><label><span>Date and time</span><input type="datetime-local" name="interviewAt" value={form.interviewAt} onChange={change} /></label><label><span>Format</span><select name="mode" value={form.mode} onChange={change}><option value="remote">Remote</option><option value="onsite">On-site</option><option value="phone">Phone</option><option value="hybrid">Hybrid</option></select></label><label className="wide"><span>Location or meeting details</span><input name="location" value={form.location} onChange={change} placeholder="Office address or meeting-link reminder" maxLength={250} /></label></div></section>
            <section><h3><UserRound /> Contact and opportunity</h3><div className="form-grid"><label><span>Recruiter / contact</span><input name="contactName" value={form.contactName} onChange={change} placeholder="Name and title" maxLength={150} /></label><label><span>Contact email</span><input type="email" name="contactEmail" value={form.contactEmail} onChange={change} placeholder="recruiter@company.com" maxLength={190} /></label><label><span>Job-post URL</span><input type="url" name="jobUrl" value={form.jobUrl} onChange={change} placeholder="https://…" maxLength={500} /></label><label><span>Compensation range</span><input name="salaryRange" value={form.salaryRange} onChange={change} placeholder="₹25L – ₹32L" maxLength={100} /></label></div></section>
            <section><h3><Target /> Preparation and interview memory</h3><div className="form-grid text-fields"><label><span>Preparation focus</span><textarea name="preparationFocus" value={form.preparationFocus} onChange={change} placeholder="Topics, projects, behavioral stories, or weak areas to review…" maxLength={5000} /></label><label><span>Questions asked</span><textarea name="questionsAsked" value={form.questionsAsked} onChange={change} placeholder="Record questions and your answers after every round…" maxLength={10000} /></label><label className="wide"><span>Notes and reflection</span><textarea name="notes" value={form.notes} onChange={change} placeholder="What went well? What should change before the next round?" maxLength={10000} /></label></div></section>
            <section><h3><CheckCircle2 /> Follow-up</h3><div className="form-grid"><label><span>Next action</span><input name="nextAction" value={form.nextAction} onChange={change} placeholder="Send thank-you note, finish assignment…" maxLength={500} /></label><label><span>Follow-up date</span><input type="date" name="followUpAt" value={form.followUpAt} onChange={change} /></label></div></section>
            {error && <div className="interview-error">{error}</div>}
          </div>
          <footer><button className="button ghost" type="button" onClick={() => setFormOpen(false)}>Cancel</button><button className="button primary" type="submit" disabled={saving}>{saving ? 'Saving…' : editingId ? 'Save changes' : 'Add interview'}</button></footer>
        </form>
      </div>}
    </div>
  );
}
